/**
 * useBackOrder — react-query hook for the storefront "back order cart".
 * Lean mirror of useCart: loads the items across the customer's open back orders (one product per
 * back order) and edits them item-by-item. Editing auto-persists; each product's BackOrder is
 * created/updated/removed live server-side.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import backOrderService from "../services/backOrderService.js";
import authService from "../services/authService.js";

const QUERY_KEY = ["backOrder"];

export const useBackOrder = () => {
  const queryClient = useQueryClient();

  const currentStore = authService.getCurrentStore();
  const isAuthenticated = authService.isAuthenticated();
  // Per-company Back Order module toggle — the feature is fully off when disabled.
  const moduleEnabled = authService.getCurrentCompany()?.moduleBackOrders === true;
  const hasStore = !!(
    isAuthenticated && currentStore?.id && !authService.isEmployee() && moduleEnabled
  );

  const { data: backOrderData, isLoading } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => backOrderService.getOpenBackOrders(),
    enabled: hasStore,
    staleTime: 1000,
    cacheTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error?.message?.includes("No store selected")) return false;
      return failureCount < 2;
    },
  });

  const setMutation = useMutation({
    mutationFn: ({ variantId, quantity, productData }) =>
      backOrderService.setItemQuantity(variantId, quantity, productData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  const backOrderItems = useMemo(
    () => backOrderData?.backOrderItems || [],
    [backOrderData],
  );
  const backOrderItemCount = backOrderItems.length;
  const backOrderUnitCount = useMemo(
    () => backOrderItems.reduce((sum, i) => sum + (i.unitQuantity || 0), 0),
    [backOrderItems],
  );

  const setBackOrderQuantity = useCallback(
    (variantId, quantity, productData) =>
      setMutation.mutateAsync({ variantId, quantity, productData }),
    [setMutation],
  );

  const getItemBackOrderQuantity = useCallback(
    (variantId) => {
      const item = backOrderItems.find((i) => i.productSkuVariantID === variantId);
      return item?.unitQuantity || 0;
    },
    [backOrderItems],
  );

  return {
    backOrder: backOrderData,
    backOrderItems,
    backOrderItemCount,
    backOrderUnitCount,
    isLoading,
    isUpdating: setMutation.isPending,
    setBackOrderQuantity,
    getItemBackOrderQuantity,
    canUseBackOrder: hasStore,
  };
};

export default useBackOrder;
